'use client';

import { createContext, useContext, ReactNode } from 'react';
import type { Locale } from './config';
import type { Dictionary } from './get-dictionary';

type LocaleContextType = {
  locale: Locale;
  dictionary: Dictionary;
};

const LocaleContext = createContext<LocaleContextType | null>(null);

export function LocaleProvider({
  children,
  locale,
  dictionary,
}: {
  children: ReactNode;
  locale: Locale;
  dictionary: Dictionary;
}) {
  return (
    <LocaleContext.Provider value={{ locale, dictionary }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale(): Locale {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context.locale;
}

// For components rendered both inside [locale] (localized) and on legacy
// non-locale routes (no provider) — returns null instead of throwing there.
export function useOptionalTranslations(): Dictionary | null {
  return useContext(LocaleContext)?.dictionary ?? null;
}

export function useTranslations(): Dictionary {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useTranslations must be used within a LocaleProvider');
  }
  return context.dictionary;
}

// Helper to get a nested translation value by path
export function useT() {
  const dictionary = useTranslations();

  return function t(path: string): string {
    const keys = path.split('.');
    let value: unknown = dictionary;

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = (value as Record<string, unknown>)[key];
      } else {
        console.warn(`Translation not found for path: ${path}`);
        return path;
      }
    }

    return typeof value === 'string' ? value : path;
  };
}
